import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Customers from './CustomersEnhanced';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CustomerDetailPage - A wrapper component for direct URL access to customer profiles
 * This handles routes like /customers/:customerId
 */
const CustomerDetailPage = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract customer ID from pathname
  const customerId = location.pathname.split('/customers/')[1];

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerId) {
        setError('No customer ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('streakfree_token');
        
        const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Customer not found');
          }
          throw new Error('Failed to load customer');
        }

        const customerData = await response.json();
        setCustomer(customerData);
        setError(null);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId]);

  const handleBack = () => {
    // Try to go back in history, or navigate to customers list
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleNavigateToWorkPlanner = (date) => {
    if (date) {
      navigate(`/work-planner?date=${date}`);
    } else {
      navigate('/work-planner');
    }
  };

  const handleNavigate = (page) => {
    navigate(`/${page}`);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading customer...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-zinc-800 mb-2">Error</h2>
          <p className="text-zinc-500 mb-4">{error}</p>
          <button 
            onClick={handleBack}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render the Customers component with the customer pre-selected
  return (
    <Customers 
      initialCustomer={customer}
      onBack={handleBack}
      onNavigateToWorkPlanner={handleNavigateToWorkPlanner}
      onNavigate={handleNavigate}
    />
  );
};

export default CustomerDetailPage;
